/**
 * useControllableState
 *
 * Small controlled/uncontrolled state helper for AI Elements.
 *
 * Primary responsibilities:
 * - Support controlled and uncontrolled component state
 * - Notify callers when state changes
 *
 * Dependencies:
 * - React
 */

import { useCallback, useState } from 'react'

interface UseControllableStateOptions<T> {
  prop?: T
  defaultProp: T
  onChange?: (value: T) => void
}

export function useControllableState<T>({
  prop,
  defaultProp,
  onChange
}: UseControllableStateOptions<T>) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultProp)
  const isControlled = prop !== undefined
  const value = isControlled ? prop : uncontrolledValue

  const setValue = useCallback(
    (nextValue: T) => {
      if (!isControlled) {
        setUncontrolledValue(nextValue)
      }

      onChange?.(nextValue)
    },
    [isControlled, onChange]
  )

  return [value, setValue] as const
}
